import React, { useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import DepthChartSkeleton from "./DepthChartSkeleton";
import MMPVariationsTable from "./MMPVariationsTable";

ChartJS.register(annotationPlugin);
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend
);

function DepthChart({
  buyOrders,
  sellOrders,
  midMarketPrice,
  osmosisPoolData,
  olswapData,
  transactions,
}) {
  const [variability, setvariability] = useState(0.7);
  const [labelsVisible, setLabelsVisible] = useState([2, 3]);

  if (
    !buyOrders |
    !sellOrders |
    !midMarketPrice |
    !osmosisPoolData |
    !olswapData
  ) {
    return (
      <div>
        <DepthChartSkeleton />
      </div>
    );
  }

  // Calculate the highest buy price and the lowest sell price
  const highestBuyPrice = Math.max(
    ...buyOrders.map((order) => parseFloat(order.price))
  );
  const lowestSellPrice = Math.min(
    ...sellOrders.map((order) => parseFloat(order.price))
  );

  if (olswapData.lastTransactionPrice > midMarketPrice * (1 + variability)) {
    setvariability(1);
  }

  // Determine the price range for filtering
  const thresholdRange = variability * highestBuyPrice;
  const buyOrdersFiltered = filterOrders(
    buyOrders,
    Math.min(highestBuyPrice, lowestSellPrice) - thresholdRange,
    "buy"
  );
  const sellOrdersFiltered = filterOrders(
    sellOrders,
    Math.max(highestBuyPrice, lowestSellPrice) + thresholdRange,
    "sell"
  );

  // Calculate cumulative values for filtered buy and sell orders
  const cumulativeBuyValues = calculateCumulativeValue(
    buyOrdersFiltered,
    "desc"
  );
  const cumulativeSellValues = calculateCumulativeValue(
    sellOrdersFiltered,
    "asc"
  );

  // Calculate leftPrice and rightPrice
  const leftPrice = Math.min(...buyOrdersFiltered.map((order) => order.price));
  const rightPrice = Math.max(
    ...sellOrdersFiltered.map((order) => order.price)
  );

  // Calculate amountToLeftPrice
  const amountToLeftPrice = cumulativeBuyValues.reduce((total, order) => {
    if (order.price > leftPrice) {
      total += order.amount * order.price;
    }
    return total;
  }, 0);

  // Calculate amountToRightPrice
  const amountToRightPrice = cumulativeSellValues.reduce((total, order) => {
    if (order.price < rightPrice) {
      total += order.amount * order.price;
    }
    return total;
  }, 0);

  const chartData = {
    datasets: [
      {
        fill: true,
        label: "Buy Orders",
        data: cumulativeBuyValues.map((order) => ({
          x: order.price,
          y: order.cumulativeValue,
        })),
        backgroundColor: "rgba(194, 245, 225, 0.3)",
        borderColor: "rgba(31, 211, 135, 1)",
        pointRadius: 0,
        tension: 0.1,
        borderWidth: 3,
      },
      {
        fill: true,
        label: "Sell Orders",
        data: cumulativeSellValues.map((order) => ({
          x: order.price,
          y: order.cumulativeValue,
        })),
        backgroundColor: "rgba(238, 195, 195, 0.3)",
        borderColor: "rgba(190, 13, 27, 1)",
        pointRadius: 0,
        tension: 0.1,
        borderWidth: 3,
      },
    ],
  };

  const greatestVolume = Math.max(
    cumulativeBuyValues[cumulativeBuyValues.length - 1].cumulativeValue,
    cumulativeSellValues[cumulativeSellValues.length - 1].cumulativeValue
  );
  const osmosisPrice = osmosisPoolData.price;
  const osmosisPriceColor =
    osmosisPrice === midMarketPrice
      ? "white"
      : osmosisPrice > midMarketPrice
      ? "rgb(40, 167, 69)"
      : "rgb(220, 53, 69)";

  const chartOptions = {
    responsive: true,
    scales: {
      x: {
        type: "linear",
        position: "bottom",
        title: {
          display: false,
          text: "Price (USDt)",
        },
        max: Math.max(
          ...sellOrdersFiltered.map((order) => parseFloat(order.price))
        ),
        min: Math.min(
          ...buyOrdersFiltered.map((order) => parseFloat(order.price))
        ),
        grid: {
          display: false,
        },
        ticks: {
          callback: function (value) {
            return value.toFixed(4);
          },
        },
      },
      y: {
        position: "left",
        beginAtZero: true,
        ticks: {
          callback: function (value, index, values) {
            return value >= 1000000
              ? `${value / 1000000}M`
              : `${value / 1000}K`;
          },
        },
      },
    },
    plugins: {
      legend: {
        onClick: function (e, legendItem, legend) {
          // Toggle the visibility
          var index = legendItem.datasetIndex;
          var ci = this.chart;
          var meta = ci.getDatasetMeta(index);

          meta.hidden = !meta.hidden;
          if (meta.hidden) {
            setLabelsVisible(labelsVisible.filter((label) => label !== index));
          } else {
            setLabelsVisible([...labelsVisible, index]);
          }
          ci.update();
        },
      },
      annotation: {
        annotations: {
          lineMMP: {
            type: "line",
            yMin: 0,
            yMax: greatestVolume,
            xMin: midMarketPrice,
            xMax: midMarketPrice,
            borderColor: "rgba(255, 165, 0, 1)",
            borderWidth: 2,
            borderDash: [8, 8],
            display: labelsVisible.includes(2),
            label: {
              backgroundColor: "rgba(255, 165, 0, 1)",
              content: ["0Lswap MMP", `$${midMarketPrice.toFixed(5)}`],
              font: {
                size: 12,
              },
              color: "white",
              display: labelsVisible.includes(2),
              position: "end",
            },
          },
          lineLTP: {
            type: "line",
            yMin: 0,
            yMax: (greatestVolume / 5) * 4,
            xMin: olswapData.lastTransactionPrice,
            xMax: olswapData.lastTransactionPrice,
            borderColor: "rgba(0, 128, 255, 1)",
            borderWidth: 2,
            borderDash: [8, 8],
            display: window.matchMedia("(min-width: 768px)").matches, // labelsVisible.includes(2),
            label: {
              backgroundColor: "rgba(0, 128, 255, 1)",
              content: ["0Lswap LTP", `$${olswapData.lastTransactionPrice}`],
              font: {
                size: 12,
              },
              color: "rgba(255, 255, 255, 1)",
              display: labelsVisible.includes(2),
              position: "end",
            },
          },
          lineOsmosis: {
            type: "line",
            yMin: 0,
            yMax: (greatestVolume / 5) * 3, // (window.innerWidth <= 768 ? 1 : 2),
            xMin: osmosisPrice,
            xMax: osmosisPrice,
            borderColor: "rgba(138, 43, 226, 1)",
            borderWidth: 2,
            borderDash: [8, 8],
            display: window.matchMedia("(min-width: 768px)").matches, // labelsVisible.includes(3),
            label: {
              backgroundColor: "rgba(138, 43, 226, 1)",
              content: function (context) {
                return [
                  "Osmosis LTP",
                  `$${osmosisPrice.toFixed(6)}` /*, formattedDifference*/,
                ];
              },
              font: {
                size: 12,
              },
              color: [
                "rgba(255, 255, 255, 1)",
                "rgba(255, 255, 255, 1)",
                osmosisPriceColor,
              ],
              display: labelsVisible.includes(3),
              position: "end",
            },
          },
        },
      },
    },
  };

  const handleVariabilityChange = (event) => {
    const newValue = event.target.value;
    setvariability(newValue);
  };

  return (
    <div className="justify-center pb-8">
      <Line options={chartOptions} data={chartData} />
      <div className="flex flex-col items-center space-y-4 max-w-md mx-auto my-4">
        <div className="w-full flex justify-between items-center">
          <div className="text-left text-green-600">
            ${leftPrice.toFixed(5)}
          </div>
          <div>{(variability * 100).toFixed(0) + " %"}</div>
          <div className="text-right text-red-600">
            ${rightPrice.toFixed(5)}
          </div>
        </div>
        <input
          type="range"
          min={0.0}
          max={1}
          step={0.01}
          value={variability}
          onChange={handleVariabilityChange}
          className="w-full h-2 bg-gray-300 rounded-lg appearance-none cursor-pointer"
        />
        <div className="w-full flex justify-between items-center">
          <div className="text-left text-green-600">
            {amountToLeftPrice.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}
          </div>
          <div className="text-center">
            {(amountToLeftPrice + amountToRightPrice).toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}
          </div>
          <div className="text-right text-red-600">
            {amountToRightPrice.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}
          </div>
        </div>
      </div>

      <MMPVariationsTable
        midMarketPrice={midMarketPrice}
        olswapData={olswapData}
        osmosisPoolData={osmosisPoolData}
      />
    </div>
  );
}

function filterOrders(orders, thresholdPrice, type) {
  return orders
    .filter((order) =>
      type === "buy"
        ? parseFloat(order.price) >= thresholdPrice
        : parseFloat(order.price) <= thresholdPrice
    )
    .map((order) => ({
      ...order,
      price: parseFloat(order.price),
      amount: parseFloat(order.amount),
      value: parseFloat(order.amount), // Assuming value is calculated as price * amount
    }));
}

function calculateCumulativeValue(orders, orderType) {
  // Sort orders and calculate cumulative value
  orders.sort((a, b) =>
    orderType === "asc" ? a.price - b.price : b.price - a.price
  );
  let cumulativeValue = 0;
  return orders.map((order) => {
    cumulativeValue += order.value;
    return {
      ...order,
      cumulativeValue: cumulativeValue,
    };
  });
}

export default DepthChart;
